"use strict";

import {onFind} from "@elements/init-modules-in-scope";
import {findIn, findAllIn, setText, setAttribute, removeAttribute, addClass, removeClass, on} from '@elements/dom-utils';

const selectors = {
    base: '.js-color-card',
    hex: '.js-color-card__value--hex',
    rgbValue: '.js-color-card__value--rgb',
    cmykValue: '.js-color-card__value--cmyk',
    copyButtons: '[class*="js-color-card__copy--"]'
};

const copiedStateDuration = 3000;

export function init () {
    onFind(selectors.base, function (baseElement) {
        let hexElement = findIn(selectors.hex, baseElement);

        if (hexElement) {
            let rgb = hexToRgb(hexElement.textContent);

            if (rgb) {
                let cmyk = rgbToCmyk(rgb);

                let rgbValueElement = findIn(selectors.rgbValue, baseElement);
                if (rgbValueElement) {
                    setText(`${rgb.r} / ${rgb.g} / ${rgb.b}`, rgbValueElement);
                }

                let cmykValueElement = findIn(selectors.cmykValue, baseElement);
                if (cmykValueElement) {
                    setText(`${cmyk.c} / ${cmyk.m} / ${cmyk.y} / ${cmyk.k}`, cmykValueElement);
                }
            }
        }

        setupCopyButtons(baseElement);
    });
}

// each copy button mirrors the text of its matching .js-color-card__value--<field>, and copies that value on click
function setupCopyButtons (baseElement) {
    findAllIn(selectors.copyButtons, baseElement).forEach((copyButton) => {
        let field = copyButton.className.match(/js-color-card__copy--(\S+)/);
        if (!field) {
            return;
        }

        let valueElement = findIn(`.js-color-card__value--${field[1]}`, baseElement);
        if (valueElement) {
            setAttribute('data-copy', valueElement.textContent.trim(), copyButton);
        }

        let originalLabel = copyButton.textContent.trim();
        let copiedLabel = copyButton.getAttribute('data-label-copied');
        let resetTimeout = null;

        on('click', function () {
            let value = copyButton.getAttribute('data-copy');
            if (!value) {
                return;
            }

            copyToClipboard(value);

            if (copiedLabel) {
                clearTimeout(resetTimeout);
                setText(copiedLabel, copyButton);
                addClass('is-copied', copyButton);
                setAttribute('disabled', 'disabled', copyButton);

                resetTimeout = setTimeout(() => {
                    setText(originalLabel, copyButton);
                    removeClass('is-copied', copyButton);
                    removeAttribute('disabled', copyButton);
                }, copiedStateDuration);
            }
        }, copyButton);
    });
}

function copyToClipboard (value) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(value).catch((error) => {
            console.error('[color-card] navigator.clipboard.writeText failed', error);
        });
        return;
    }

    let textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    try {
        document.execCommand('copy');
    } catch (error) {
        console.error('[color-card] execCommand("copy") failed', error);
    }

    document.body.removeChild(textarea);
}

function hexToRgb (hex) {
    let normalized = hex.trim().replace('#', '');

    if (normalized.length === 3) {
        normalized = normalized.split('').map((char) => char + char).join('');
    }

    if (!/^[0-9a-fA-F]{6}$/.test(normalized)) {
        return null;
    }

    return {
        r: parseInt(normalized.substring(0, 2), 16),
        g: parseInt(normalized.substring(2, 4), 16),
        b: parseInt(normalized.substring(4, 6), 16)
    };
}

function rgbToCmyk ({r, g, b}) {
    let rRatio = r / 255;
    let gRatio = g / 255;
    let bRatio = b / 255;

    let k = 1 - Math.max(rRatio, gRatio, bRatio);

    if (k === 1) {
        return {c: 0, m: 0, y: 0, k: 100};
    }

    return {
        c: Math.round((1 - rRatio - k) / (1 - k) * 100),
        m: Math.round((1 - gRatio - k) / (1 - k) * 100),
        y: Math.round((1 - bRatio - k) / (1 - k) * 100),
        k: Math.round(k * 100)
    };
}
